import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWorkflows, saveWorkflow, deleteWorkflow } from "../../redux/actions/workflowActions";
import { fetchSections, saveSection, deleteSection, fetchAvailableFields } from "../../redux/actions/sectionActions";
import Swal from "sweetalert2";
import "./ParamInfosUsers.css";

// Initial rules and sections removed to use dynamic ones
const SECTION_STYLE = {
    "Contact":                { color: "#059669", bg: "#d1fae5" },
    "Informations bancaires": { color: "#707ea7", bg: "#ede9fe" },
    "Photo de profil":        { color: "#707ea7", bg: "#f9fefd" },
    "Photo de bannière":      { color: "#dc3545", bg: "#fde8e7" },
};

const MODE_CONFIG = {
    libre: { label: "Validation libre",    color: "#059669", bg: "#d1fae5" },
    admin: { label: "Validation admin",    color: "#707ea7", bg: "#f9fefd" },
    delai: { label: "Avec délai",          color: "#707ea7", bg: "#ede9fe" },
    notif: { label: "Notification seule",  color: "#2f4595", bg: "#e8edf7" },
};

const MODE_DESCRIPTIONS = {
    libre: "La modification est appliquée immédiatement, sans aucune validation requise.",
    admin: "La modification est mise en attente jusqu'à l'approbation d'un administrateur.",
    delai: "La modification est auto-approuvée après le délai configuré si l'admin n'agit pas.",
    notif: "La modification est appliquée immédiatement, l'admin est simplement notifié.",
};

const emptyForm = {
    sectionId:         "",
    fieldKey:          "",
    champ:             "",
    description:       "",
    mode:              "libre",
    delai:             24,
    isActive:          true,
    notifAdminModif:   true,
    notifAdminPending: false,
    notifUserConfirm:  true,
    notifUserStatus:   true,
};

// L'ID sera géré par le backend

/* ─── Composant ──────────────────────────────────────────────────── */

const ParamInfosUsers = () => {
    const dispatch = useDispatch();
    const { list: rules, loading, error } = useSelector((state) => state.userWorkflows);
    const { list: sections, availableFields } = useSelector((state) => state.userSections);
    const companyId = useSelector((state) => state.auth?.user?.companyId);

    const [mainTab,       setMainTab]       = useState("rules"); // "rules" ou "sections"
    const [q,             setQ]             = useState("");
    const [filterSection, setFilterSection] = useState("Tous");
    const [isModalOpen,   setIsModalOpen]   = useState(false);
    const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
    const [editing,       setEditing]       = useState(null);
    const [editingSection,setEditingSection]= useState(null);
    const [form,          setForm]          = useState(emptyForm);
    const [sectionForm,   setSectionForm]   = useState({ name: "", displayOrder: 0 });
    const [activeTab,     setActiveTab]     = useState("info");
    const [alertMsg,      setAlertMsg]      = useState(null);

    useEffect(() => {
        if (companyId) {
            dispatch(fetchWorkflows(companyId));
            dispatch(fetchSections(companyId));
            dispatch(fetchAvailableFields());
        }
    }, [dispatch, companyId]);

    /* ── Filtrage ── */
    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        return rules.filter((r) => {
            const sectionName = r.section?.name || sections.find(s => s.id === r.section?.id)?.name || "";
            const matchQ =
                !term ||
                (r.fieldKey    || "").toLowerCase().includes(term) ||
                (sectionName   || "").toLowerCase().includes(term) ||
                (r.description || "").toLowerCase().includes(term);
            const matchS = filterSection === "Tous" || sectionName === filterSection;
            return matchQ && matchS;
        });
    }, [rules, q, filterSection]);

    /* ── Alert ── */
    const showAlert = (msg, type = "success") => {
        setAlertMsg({ msg, type });
        setTimeout(() => setAlertMsg(null), 2500);
    };

    /* ── Helpers modal ── */
    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("info");
        setIsModalOpen(true);
    };

    const openEdit = (rule) => {
        setEditing(rule);
        setForm({ 
            ...rule,
            sectionId: rule.section?.id || "",
            mode: rule.validationMode ? rule.validationMode.toLowerCase() : "libre",
            delai: rule.delay || 24,
            champ: availableFields.find(f => f.key === rule.fieldKey)?.label || rule.fieldKey
        });
        setActiveTab("info");
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("info");
    };

    const openCreateSection = () => {
        setEditingSection(null);
        setSectionForm({ name: "", displayOrder: sections.length + 1 });
        setIsSectionModalOpen(true);
    };

    const closeSectionModal = () => {
        setIsSectionModalOpen(false);
        setEditingSection(null);
    };

    /* ── onChange générique ── */
    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        const next = type === "checkbox" ? checked : value;

        setForm((prev) => {
            let updated = { ...prev, [name]: next };
            if (name === "fieldKey") {
                const champObj = availableFields.find(f => f.key === value);
                updated.champ = champObj?.label || value;
            }
            return updated;
        });
    };

    const onSectionChange = (e) => {
        const { name, value } = e.target;
        setSectionForm(prev => ({ ...prev, [name]: value }));
    };

    /* ── Soumissions ── */
    const handleSectionSubmit = (e) => {
        e.preventDefault();
        if (!sectionForm.name) return;
        dispatch(saveSection({ ...sectionForm, companyId }));
        closeSectionModal();
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.sectionId || !form.fieldKey) {
            Swal.fire({ icon: "warning", text: "La section et le champ sont obligatoires." });
            return;
        }

        if (!editing) {
            const exists = rules.find(
                (r) => r.section?.id === Number(form.sectionId) && r.fieldKey === form.fieldKey
            );
            if (exists) {
                const secName = sections.find(s => s.id === Number(form.sectionId))?.name || "cette section";
                Swal.fire({
                    icon: "warning",
                    text: `Une règle existe déjà pour le champ "${form.fieldKey}" dans ${secName}.`,
                });
                return;
            }
        }

        const payload = {
            ...form,
            section: { id: Number(form.sectionId) },
            companyId: companyId,
            validationMode: form.mode.toUpperCase(), // Backend uses enum in uppercase
            delay: form.mode === "delai" ? Number(form.delai) : null,
        };

        dispatch(saveWorkflow(payload));
        closeModal();
    };

    /* ── Suppression ── */
    const handleDelete = (rule) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer la règle pour "${rule.fieldKey}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(deleteWorkflow(rule.id));
                showAlert("Règle supprimée.");
            }
        });
    };

    const handleDeleteSection = (sec) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer la section "${sec.name}" ? Attention, cela supprimera peut-être les règles associées.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#dc3545",
        }).then((res) => {
            if (res.isConfirmed) {
                dispatch(deleteSection(sec.id));
            }
        });
    };

    /* ── Toggle actif ── */
    const handleToggleActive = (rule) => {
        dispatch(saveWorkflow({ ...rule, section: { id: rule.section?.id }, isActive: !rule.isActive }));
    };

    /* ─── Render ─────────────────────────────────────────────────── */
    return (
        <div className="piv-page">

            {/* Tabs Principales */}
            <div className="piv-tabs" style={{ marginBottom: '20px' }}>
                <button
                    className={`piv-tab ${mainTab === "rules" ? "active" : ""}`}
                    onClick={() => setMainTab("rules")}
                >
                    Règles de validation
                </button>
                <button
                    className={`piv-tab ${mainTab === "sections" ? "active" : ""}`}
                    onClick={() => setMainTab("sections")}
                >
                    Gestion des Sections
                </button>
            </div>

            {mainTab === "rules" ? (
                <>
                    <div className="piv-header">
                        <div>
                            <h2>Paramétrage des validations</h2>
                            <p className="piv-subtitle">
                                Définissez les règles d'approbation pour chaque champ
                            </p>
                        </div>
                        <button className="piv-btn piv-btn-primary" onClick={openCreate}>
                            + Ajouter une règle
                        </button>
                    </div>

                    <div className="piv-toolbar">
                        <input
                            className="piv-search"
                            placeholder="Rechercher par champ, section..."
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />

                        <div className="piv-filter-tabs">
                            <button
                                className={`piv-filter-tab ${filterSection === "Tous" ? "active" : ""}`}
                                onClick={() => setFilterSection("Tous")}
                            >
                                Tous
                            </button>
                            {sections.map((sec) => (
                                <button
                                    key={sec.id}
                                    className={`piv-filter-tab ${filterSection === sec.name ? "active" : ""}`}
                                    onClick={() => setFilterSection(sec.name)}
                                >
                                    {sec.name}
                                </button>
                            ))}
                        </div>

                        <div className="piv-meta">
                            {filtered.length} règle{filtered.length !== 1 ? "s" : ""}
                        </div>
                    </div>

                    {/* Table Règles */}
            <div className="piv-table-wrapper">
                <table className="piv-table">
                    <thead>
                    <tr>
                        <th>Section</th>
                        <th>Champ</th>
                        <th>Mode de validation</th>
                        <th>Notif. admin</th>
                        <th>Délai</th>
                        <th>Statut</th>
                        <th style={{ width: 140, textAlign: "right" }}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filtered.map((rule) => {
                        const secName = rule.section?.name || sections.find(s => s.id === rule.section?.id)?.name || "Contact";
                        const ss = SECTION_STYLE[secName] || SECTION_STYLE["Contact"];
                        const modeKey = rule.validationMode?.toLowerCase() || rule.mode || "libre";
                        const ml = MODE_CONFIG[modeKey] || {};

                        return (
                            <tr key={rule.id}>
                                <td>
                                        <span className="piv-badge" style={{ color: ss.color, background: ss.bg }}>
                                            {secName}
                                        </span>
                                </td>

                                <td>
                                    <div className="piv-champ-cell">
                                        <div className="piv-champ-dot" />
                                        <span className="piv-champ-name">{rule.fieldKey}</span>
                                    </div>
                                </td>

                                <td>
                                        <span className="piv-badge" style={{ color: ml.color, background: ml.bg }}>
                                            {ml.label}
                                        </span>
                                </td>

                                <td>
                                    {rule.notifAdminModif
                                        ? <span className="piv-notif-chip">✓ Oui</span>
                                        : <span className="piv-no">Non</span>
                                    }
                                </td>

                                <td className={modeKey === "delai" ? "piv-delai-val" : "piv-delai-none"}>
                                    {modeKey === "delai" && rule.delay ? `${rule.delay}h` : "—"}
                                </td>

                                <td>
                                    <button
                                        className={`piv-toggle ${rule.isActive ? "on" : "off"}`}
                                        onClick={() => handleToggleActive(rule)}
                                        title={rule.isActive ? "Désactiver" : "Activer"}
                                    >
                                        <span className="piv-toggle-knob" />
                                        <span className="piv-toggle-label">
                                                {rule.isActive ? "Actif" : "Inactif"}
                                            </span>
                                    </button>
                                </td>

                                <td className="piv-row-actions">
                                    <button className="piv-link-btn" onClick={() => openEdit(rule)}>
                                        Modifier
                                    </button>
                                    <button className="piv-link-btn danger" onClick={() => handleDelete(rule)}>
                                        Supprimer
                                    </button>
                                </td>
                            </tr>
                        );
                    })}

                    {filtered.length === 0 && (
                        <tr>
                            <td colSpan={7} className="piv-empty">
                                Aucune règle de validation trouvée
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
            </>
            ) : (
                <>
                    <div className="piv-header">
                        <div>
                            <h2>Gestion des Sections</h2>
                            <p className="piv-subtitle">
                                Créez et gérez les groupes de champs pour l'affichage
                            </p>
                        </div>
                        <button className="piv-btn piv-btn-primary" onClick={openCreateSection}>
                            + Ajouter une section
                        </button>
                    </div>
                    
                    <div className="piv-table-wrapper">
                        <table className="piv-table">
                            <thead>
                                <tr>
                                    <th>Nom de la section</th>
                                    <th>Ordre d'affichage</th>
                                    <th style={{ width: 140, textAlign: "right" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sections.map(sec => (
                                    <tr key={sec.id}>
                                        <td>{sec.name}</td>
                                        <td>{sec.displayOrder}</td>
                                        <td className="piv-row-actions">
                                            <button className="piv-link-btn" onClick={() => { setEditingSection(sec); setSectionForm(sec); setIsSectionModalOpen(true); }}>Modifier</button>
                                            <button className="piv-link-btn danger" onClick={() => handleDeleteSection(sec)}>Supprimer</button>
                                        </td>
                                    </tr>
                                ))}
                                {sections.length === 0 && (
                                    <tr><td colSpan={3} className="piv-empty">Aucune section trouvée</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Modal Règles */}
            {isModalOpen && (
                <div className="piv-overlay" onClick={closeModal}>
                    <div className="piv-modal" onClick={(e) => e.stopPropagation()}>

                        <div className="piv-modal-header">
                            <h3>
                                {editing ? "Modifier la règle de validation" : "Ajouter une règle de validation"}
                            </h3>
                            <button className="piv-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <div className="piv-tabs">
                            {[
                                { key: "info",  label: "Identification" },
                                { key: "valid", label: "Validation" },
                                { key: "notif", label: "Notifications" },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={`piv-tab ${activeTab === key ? "active" : ""}`}
                                    onClick={() => setActiveTab(key)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        <form className="piv-form" onSubmit={handleSubmit}>

                            {/* ── Onglet Identification ── */}
                            {activeTab === "info" && (
                                <div className="piv-grid">
                                    <div className="piv-field">
                                        <label>Section *</label>
                                        <select
                                            name="sectionId"
                                            value={form.sectionId}
                                            onChange={onChange}
                                            disabled={!!editing}
                                        >
                                            <option value="">-- Choisir une section --</option>
                                            {sections.map((s) => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                        {editing && (
                                            <span className="piv-hint">
                                                La section ne peut pas être modifiée après création.
                                            </span>
                                        )}
                                    </div>

                                    <div className="piv-field">
                                        <label>Champ *</label>
                                        <select
                                            name="fieldKey"
                                            value={form.fieldKey}
                                            onChange={onChange}
                                            disabled={!!editing}
                                        >
                                            <option value="">-- Choisir un champ --</option>
                                            {availableFields.map((f) => (
                                                <option key={f.key} value={f.key}>{f.label} ({f.key})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="piv-field piv-full">
                                        <label>Description</label>
                                        <textarea
                                            name="description"
                                            value={form.description}
                                            onChange={onChange}
                                            rows={3}
                                            placeholder="Décrivez la raison de cette règle de validation..."
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ── Onglet Validation ── */}
                            {activeTab === "valid" && (
                                <div className="piv-grid">
                                    <div className="piv-field piv-full">
                                        <label>Mode de validation *</label>
                                        <div className="piv-modes-grid">
                                            {Object.entries(MODE_CONFIG).map(([key, cfg]) => (
                                                <label
                                                    key={key}
                                                    className={`piv-mode-card ${form.mode === key ? "selected" : ""}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="mode"
                                                        value={key}
                                                        checked={form.mode === key}
                                                        onChange={onChange}
                                                    />
                                                    <div className="piv-mode-card-body">
                                                        <span className="piv-mode-card-title" style={{ color: cfg.color }}>
                                                            {cfg.label}
                                                        </span>
                                                        <span className="piv-mode-card-desc">
                                                            {MODE_DESCRIPTIONS[key]}
                                                        </span>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>

                                    {form.mode === "delai" && (
                                        <div className="piv-field">
                                            <label>Délai avant approbation auto (heures)</label>
                                            <input
                                                type="number"
                                                name="delai"
                                                value={form.delai}
                                                onChange={onChange}
                                                min={1}
                                                max={168}
                                                placeholder="Ex : 24"
                                            />
                                            <span className="piv-hint">
                                                Si l'admin n'agit pas dans ce délai, la modification est auto-approuvée.
                                            </span>
                                        </div>
                                    )}

                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={form.isActive}
                                                onChange={onChange}
                                            />
                                            Règle active
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* ── Onglet Notifications ── */}
                            {activeTab === "notif" && (
                                <div className="piv-grid">
                                    <div className="piv-field piv-full">
                                        <div className="piv-notif-section-title">Notifications administrateur</div>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifAdminModif" checked={form.notifAdminModif} onChange={onChange} />
                                            Notifier l'admin lors d'une modification
                                        </label>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifAdminPending" checked={form.notifAdminPending} onChange={onChange} />
                                            Rappel quotidien des demandes en attente
                                        </label>
                                    </div>

                                    <div className="piv-field piv-full">
                                        <div className="piv-notif-section-title">Notifications utilisateur</div>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifUserConfirm" checked={form.notifUserConfirm} onChange={onChange} />
                                            Notifier l'utilisateur de la réception de sa demande
                                        </label>
                                    </div>
                                    <div className="piv-field piv-full">
                                        <label className="piv-check-label">
                                            <input type="checkbox" name="notifUserStatus" checked={form.notifUserStatus} onChange={onChange} />
                                            Notifier l'utilisateur du statut (approuvé / refusé)
                                        </label>
                                    </div>
                                </div>
                            )}

                            <div className="piv-modal-actions">
                                <button type="button" className="piv-btn piv-btn-outline" onClick={closeModal}>
                                    Annuler
                                </button>
                                <button type="submit" className="piv-btn piv-btn-primary">
                                    {editing ? "Enregistrer" : "Créer la règle"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Section */}
            {isSectionModalOpen && (
                <div className="piv-overlay" onClick={closeSectionModal}>
                    <div className="piv-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
                        <div className="piv-modal-header">
                            <h3>{editingSection ? "Modifier la section" : "Ajouter une section"}</h3>
                            <button className="piv-modal-close" onClick={closeSectionModal}>✕</button>
                        </div>
                        <form className="piv-form" onSubmit={handleSectionSubmit}>
                            <div className="piv-grid" style={{ gridTemplateColumns: '1fr' }}>
                                <div className="piv-field">
                                    <label>Nom de la section *</label>
                                    <input required type="text" name="name" value={sectionForm.name} onChange={onSectionChange} />
                                </div>
                                <div className="piv-field">
                                    <label>Ordre d'affichage</label>
                                    <input type="number" name="displayOrder" value={sectionForm.displayOrder} onChange={onSectionChange} />
                                </div>
                            </div>
                            <div className="piv-modal-actions" style={{ marginTop: 20 }}>
                                <button type="button" className="piv-btn piv-btn-outline" onClick={closeSectionModal}>Annuler</button>
                                <button type="submit" className="piv-btn piv-btn-primary">Enregistrer</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ParamInfosUsers;